import React, { FC } from 'react';
import sanitizeHtml from 'sanitize-html';
import mustache from 'mustache';
import { useSettings } from '../../../../hooks/useSettings';
import {
  useAnalyticsPost,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
  NarvarEventTypes,
} from '../../../../hooks/Analytics';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import { Item } from '../../../../../../__generated__/loma';
import Paragraph from '../../../ui/Paragraph/Paragraph';
import { SmallHeader } from '../../../ui/Header/Header';
import ProductThumbnail from '../../../ui/ProductThumbnail';
import { currencyLocaleMap } from '../localeToCurrency';
import styles from './ItemTile.module.css';

const renderOptions = { escape: (x: string): string => x };

const safeRender = (template: string, view: object): string => {
  try {
    return mustache.render(template, view, undefined, renderOptions);
  } catch {
    return '';
  }
};

interface ItemTileProps {
  itemInfo: Item;
  itemTextTemplates: Record<string, unknown>;
  featureName?: string;
}

// do not allow html tags in item attribute text
const safetyHtml = (text: string) => sanitizeHtml(text, { allowedTags: [] });

const ItemTile: FC<ItemTileProps> = ({
  itemInfo,
  itemTextTemplates,
  featureName,
}) => {
  const logEvent = useAnalyticsPost();
  const { itemsInThisShipment } = useSettings();
  const {
    state: { localeCountry, localeLanguage },
  } = usePageContext();
  const { imageUrl: imageUrlTemplate = '', url: itemUrlTemplate = '' } =
    itemsInThisShipment ?? {};

  // Currently the "hack" to not show the product images is setting the
  // image template setting to ' '. In an upcoming refresh of the items
  // in this shipment component settings, there will be a boolean flag
  // we'll be able to check instead of looking for ' '.
  const hideProductImage = imageUrlTemplate === ' ';

  const { price } = itemInfo;
  const tempPrice = price;
  let itemInfoPriceFormatted = itemInfo;

  // check if price contains any possible currency signs. otherwise, format to locale currency
  const alreadyFormatted =
    tempPrice && tempPrice.replaceAll(/[.,]/g, '').match(/[\D]/);

  if (price && !alreadyFormatted) {
    const priceNumber = parseFloat(price);

    const locale =
      localeLanguage && localeCountry
        ? `${localeLanguage}-${localeCountry}`
        : (localeLanguage ?? 'en-US');

    const currency =
      currencyLocaleMap[locale as keyof typeof currencyLocaleMap] || 'USD';

    const formattedPrice = Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(priceNumber);

    itemInfoPriceFormatted = {
      ...itemInfo,
      price: formattedPrice,
    };
  }

  const itemTitle =
    itemTextTemplates.itemsInShipmentItemTitle &&
    typeof itemTextTemplates.itemsInShipmentItemTitle === 'string'
      ? safeRender(
          itemTextTemplates.itemsInShipmentItemTitle,
          itemInfoPriceFormatted,
        )
      : '';

  let thumbnail;
  if (!hideProductImage) {
    thumbnail = (
      <ProductThumbnail
        productInfo={itemInfoPriceFormatted}
        urlTemplate={itemUrlTemplate || undefined}
        imageUrlTemplate={imageUrlTemplate || undefined}
        renderAsLink={false}
      />
    );
  }

  const itemBlock = (
    <div className={styles.itemBlock}>
      {thumbnail && (
        <div className={styles.thumbnailContainer}>{thumbnail}</div>
      )}
      <div className={styles.textContainer}>
        {itemTitle && (
          <SmallHeader as="p">
            <span dangerouslySetInnerHTML={{ __html: safetyHtml(itemTitle) }} />
          </SmallHeader>
        )}
        {[
          itemTextTemplates.itemsInShipmentAttribute0,
          itemTextTemplates.itemsInShipmentAttribute1,
          itemTextTemplates.itemsInShipmentAttribute2,
          itemTextTemplates.itemsInShipmentAttribute3,
        ].map((template, index) => {
          if (!template || typeof template !== 'string') return null;
          const renderedText = safeRender(template, itemInfoPriceFormatted);
          if (!renderedText) return null;
          return (
            <Paragraph
              key={`${index}_${itemTitle}_${renderedText}`}
              containerCustomStyle={{
                margin: '0.25rem 0',
                overflowWrap: 'break-word',
              }}
            >
              {safetyHtml(renderedText)}
            </Paragraph>
          );
        })}
      </div>
    </div>
  );

  const itemUrl = safeRender(itemUrlTemplate || '', itemInfoPriceFormatted);

  const componentEventLog = {
    ['shipments']: {
      clickComponent: NarvarClickSubTypes.SHIPMENTS,
      eventComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
    },
    ['itemsInThisShipment']: {
      clickComponent: NarvarClickSubTypes.ITEMS_IN_THIS_SHIPMENT,
      eventComponent: NarvarEventComponents.NTH_PACK_ITEMS_IN_THIS_SHIPMENT,
    },
  };

  return (
    <div className={styles.container}>
      {itemUrl ? (
        <a
          className={styles.tileLink}
          href={itemUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(): void =>
            logEvent({
              clickLinkDestinationUrl: itemUrl,
              clickLinkLabel: itemUrl,
              clickType: NarvarClickTypes.OUTGOING,
              eventType: NarvarEventTypes.CLICK,
              feature: featureName,
              ...(featureName === 'shipments' ||
              featureName === 'itemsInThisShipment'
                ? {
                    clickCategory:
                      componentEventLog[featureName].clickComponent,
                    clickSubType: componentEventLog[featureName].clickComponent,
                    eventComponent:
                      componentEventLog[featureName].eventComponent,
                    eventFeature: componentEventLog[featureName].eventComponent,
                    pageComponent:
                      componentEventLog[featureName].eventComponent,
                  }
                : {}),
            })
          }
        >
          {itemBlock}
        </a>
      ) : (
        itemBlock
      )}
    </div>
  );
};

export default ItemTile;
